import { HttpError } from './http-error'
import type { ScanConfigResponse, ScanFailureReason, SubmitScanRequest } from './types'

/** The four public scan routes (architecture/services/integration-faceheart.md §5). Every call
 * carries the scoped access token `verify` returned — never the link token itself. */
export interface ScanClient {
  getScanConfig(accessToken: string): Promise<ScanConfigResponse>
  startScan(accessToken: string): Promise<void>
  submitScan(accessToken: string, request: SubmitScanRequest): Promise<void>
  /**
   * One attempt that produced nothing, reported so the SERVICE can count it.
   *
   * This app does not say how many there have been and must not: the page reloads itself when a
   * stage stops moving, up to twice, and a counter in a tab does not survive that - so a patient
   * could fail three times and be counted once. The count decides whether a person is asked to look
   * at this patient, which is not a browser's to assert.
   */
  reportScanFailure(accessToken: string, reason: ScanFailureReason): Promise<void>
}

/** GET /v1/public/scan and the two POSTs answer 404 for both "no such assessment" and "assessment
 * is terminal or past expiry" — the service deliberately does not distinguish them (architecture §5
 * D-5 Q-2: "404, identical"), so this app must not either. */
export class ScanUnavailableError extends Error {
  constructor() {
    super('This scan is no longer available.')
    this.name = 'ScanUnavailableError'
  }
}

export function createScanClient(baseUrl: string): ScanClient {
  const authorized = (accessToken: string): HeadersInit => ({
    Authorization: `Bearer ${accessToken}`,
  })

  return {
    async getScanConfig(accessToken) {
      const response = await fetch(`${baseUrl}/v1/public/scan`, {
        headers: authorized(accessToken),
      })
      if (response.status === 404) {
        throw new ScanUnavailableError()
      }
      if (!response.ok) {
        throw new HttpError(response.status, `GET /v1/public/scan failed with ${response.status}`)
      }
      return (await response.json()) as ScanConfigResponse
    },

    async startScan(accessToken) {
      const response = await fetch(`${baseUrl}/v1/public/scan/start`, {
        method: 'POST',
        headers: authorized(accessToken),
      })
      if (response.status === 404) {
        throw new ScanUnavailableError()
      }
      if (!response.ok) {
        throw new HttpError(response.status, `start failed with ${response.status}`)
      }
    },

    async reportScanFailure(accessToken, reason) {
      const response = await fetch(`${baseUrl}/v1/public/scan/failed`, {
        method: 'POST',
        headers: { ...authorized(accessToken), 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      })
      // 404 is the service saying there is nothing left to fail - an assessment already terminal,
      // already given up on, or past its expiry. It is not news: the patient is being shown the end
      // of their journey either way, and the caller treats it like any other outcome of a report it
      // makes on the way out.
      if (!response.ok && response.status !== 404) {
        throw new HttpError(response.status, `failure report failed with ${response.status}`)
      }
    },

    async submitScan(accessToken, request) {
      // Height and weight are never on this body (architecture §5 step 4; catalogue row 57 on the
      // service side asserts their absence) — they were consumed by startMeasuring in the browser
      // and go no further. SubmitScanRequest carries only what the SDK measured.
      const response = await fetch(`${baseUrl}/v1/public/scan/submit`, {
        method: 'POST',
        headers: { ...authorized(accessToken), 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
      })
      if (response.status === 404) {
        throw new ScanUnavailableError()
      }
      if (!response.ok) {
        throw new HttpError(response.status, `submit failed with ${response.status}`)
      }
    },
  }
}
